import {
  Award,
  Coins,
  Gift,
  Loader2,
  Lock,
  Sparkles,
  Star,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/auth-context';
import type { UserBadge, UserBox } from '@api/generated/types.gen';
import { useMyBadges, useMyBalance, useMyBoxes } from './use-rewards';

function BoxTile({ box }: { box: UserBox }) {
  return (
    <div className="relative flex flex-col items-center gap-2 rounded-2xl border border-primary/30 bg-card/70 p-4 text-center backdrop-blur-sm">
      <span className="absolute right-2 top-2 rounded-full bg-background/70 p-1">
        <Lock className="h-3 w-3 text-muted-foreground" />
      </span>
      <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl bg-primary/10 ring-2 ring-primary/30">
        {box.imageUrl ? (
          <img src={box.imageUrl} alt={box.name} className="h-full w-full object-cover" />
        ) : (
          <Gift className="h-7 w-7 text-primary" />
        )}
      </div>
      <p className="truncate text-sm font-semibold text-foreground">{box.name}</p>
      <p className="text-xs text-muted-foreground">Unopened</p>
    </div>
  );
}

// Rarity → accent ring/text. Keeps badges visually tiered even before art lands.
const rarityStyles: Record<string, string> = {
  COMMON: 'ring-zinc-400/40 text-zinc-400',
  UNCOMMON: 'ring-green-500/50 text-green-500',
  RARE: 'ring-blue-500/50 text-blue-500',
  EPIC: 'ring-purple-500/50 text-purple-500',
  LEGENDARY: 'ring-amber-500/60 text-amber-500',
  MYTHIC: 'ring-fuchsia-500/60 text-fuchsia-500',
  LEGACY: 'ring-rose-500/50 text-rose-500',
};

function StatCard({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: LucideIcon;
  label: string;
  value: string | number;
  accent: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-border/60 bg-card/70 p-4 backdrop-blur-sm">
      <span
        className={cn(
          'flex h-11 w-11 shrink-0 items-center justify-center rounded-full',
          accent,
        )}
      >
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className="text-xl font-bold text-foreground">{value}</div>
      </div>
    </div>
  );
}

function BadgeTile({ badge }: { badge: UserBadge }) {
  const rarity = rarityStyles[badge.rarity] ?? rarityStyles.COMMON;
  const image = badge.iconUrl ?? badge.imageUrl;
  return (
    <div className="flex flex-col items-center gap-2 rounded-2xl border border-border/60 bg-card/70 p-4 text-center backdrop-blur-sm">
      <div
        className={cn(
          'flex h-16 w-16 items-center justify-center overflow-hidden rounded-full ring-2',
          rarity,
        )}
      >
        {image ? (
          <img src={image} alt={badge.name} className="h-full w-full object-cover" />
        ) : (
          <span className="text-xl font-bold">
            {badge.name.charAt(0).toUpperCase()}
          </span>
        )}
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-foreground">
          {badge.name}
        </p>
        <p className={cn('text-xs font-medium', rarity)}>
          {badge.rarity.charAt(0) + badge.rarity.slice(1).toLowerCase()}
          {badge.tier > 1 ? ` · Tier ${badge.tier}` : ''}
        </p>
      </div>
    </div>
  );
}

export function RewardsPage() {
  const { isAuthenticated } = useAuth();
  const { data: balance, isLoading: balanceLoading } = useMyBalance(isAuthenticated);
  const {
    data: badges,
    isLoading: badgesLoading,
    isError: badgesError,
  } = useMyBadges(isAuthenticated);

  const { data: boxes } = useMyBoxes(isAuthenticated);

  const totalXp = balance?.xp.totalXp ?? 0;
  const level = balance?.xp.level ?? 1;
  const tokens = balance?.tokenBalance.balance ?? 0;
  const unopenedBoxes = (boxes ?? []).filter((b) => b.status === 'unopened');

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-10 space-y-6">
      <div>
        <h1 className="text-2xl font-bold sm:text-3xl">My Rewards</h1>
        <p className="text-sm text-muted-foreground">
          XP, tokens, and badges you've earned.
        </p>
      </div>

      {balanceLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatCard
            icon={Sparkles}
            label="Level"
            value={level}
            accent="bg-orange-500/10 text-orange-500"
          />
          <StatCard
            icon={Star}
            label="Total XP"
            value={totalXp.toLocaleString()}
            accent="bg-yellow-500/10 text-yellow-500"
          />
          <StatCard
            icon={Coins}
            label="Tokens"
            value={tokens.toLocaleString()}
            accent="bg-amber-500/10 text-amber-500"
          />
        </div>
      )}

      {unopenedBoxes.length > 0 && (
        <div>
          <div className="mb-3 flex items-center gap-2">
            <Gift className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Packs</h2>
          </div>
          <div className="mb-3 flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5 shrink-0 text-primary" />
            Open your packs in the Overwolf desktop app to claim what's inside.
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {unopenedBoxes.map((box) => (
              <BoxTile key={box.id} box={box} />
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="mb-3 flex items-center gap-2">
          <Award className="h-5 w-5 text-violet-500" />
          <h2 className="text-lg font-semibold">Badges</h2>
        </div>
        {badgesLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : badgesError ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Couldn't load your badges. Please try again later.
          </p>
        ) : !badges || badges.length === 0 ? (
          <div className="flex flex-col items-center rounded-2xl border border-dashed border-border/60 py-12 text-center">
            <Award className="mb-3 h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm font-medium">No badges yet</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Submit a bracket or join an event to start earning.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {badges.map((badge) => (
              <BadgeTile key={badge.id} badge={badge} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
